import React, { useCallback, useMemo } from 'react';

import { useIntl, defineMessages } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import DepartmentIcon from '@atlaskit/icon/core/department';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react/component-with-fg';
import { Stack } from '@atlaskit/primitives/compiled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { TeamsAppAnalyticsContext } from '@atlaskit/teams-app-internal-analytics/analytics-context';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';
import { TeamsLinkButton } from '@atlaskit/teams-app-internal-navigation/teams-link-button';

import { HierarchyReportsOverflowCard } from './HierarchyReportsOverflowCard';
import { HierarchyUserCard } from './HierarchyUserCard';
import { ChartActionsRow, ReportsList, VerticalChartLine } from './styles';
import { type HierarchyData, type HierarchyUser } from './types';

/** Max report cards before replacing the last slot with “See N more”. */
const MAX_REPORT_CARDS = 8;

interface HierarchyChartProps {
	data: HierarchyData;
	cloudId: string;
	accountId: string;
	orgId?: string;
	testId?: string;
	onNodeClick?: (accountId: string) => void;
	/** When true, renders a collapsed preview: caps managers at 1 (with overflow tile), caps reports at 8 (with overflow tile), and shows the "Full reporting line" button. */
	isPreview?: boolean;
	/** Called when either the managers or reports overflow tile is clicked, or the "Full reporting line" button is clicked. Only used when isPreview is true. */
	onViewFullReportingLine?: () => void;
	/** The account ID of the currently logged-in user (viewer). Required to show the profile card on hover. */
	requestingUserId?: string;
}

const messages = defineMessages({
	fullReportingLineLink: {
		id: 'reporting-lines.hierarchy-chart.full-reporting-line-link',
		defaultMessage: 'Full reporting line',
		description: 'Label on the full reporting line link',
	},
});

const NoopComponent = () => null;

function partitionReports(
	reports: HierarchyUser[],
	maxReports?: number,
): {
	visibleReports: HierarchyUser[];
	overflow?: { hiddenPreview: HierarchyUser[]; hiddenCount: number };
} {
	if (maxReports === undefined || reports.length <= maxReports) {
		return { visibleReports: reports };
	}
	const visibleCount = maxReports - 1;
	const hiddenReports = reports.slice(visibleCount);
	return {
		visibleReports: reports.slice(0, visibleCount),
		overflow: {
			hiddenPreview: hiddenReports,
			hiddenCount: hiddenReports.length,
		},
	};
}

/**
 * Displays a simple hierarchy chart (manager → user → reports) for a given user.
 *
 * @internal Not ready for consumption. Do not use in production.
 */
export const HierarchyChart: React.FC<HierarchyChartProps & React.RefAttributes<unknown>> =
	componentWithFG('pt_hierarchy_chart_enabled', NoopComponent, HierarchyChartWithAnalytics);

/**
 * Wraps the chart in a `TeamsAppAnalyticsContext` so all native events emitted by the chart
 * carry `source: 'hierarchyChart'`. The chart body uses {@link useAnalyticsEvents} to fire
 * role-specific click events ({@code managerNode} / {@code directReportNode} / {@code peerNode} /
 * {@code overflow} / {@code viewFullReportingLine}).
 */
function HierarchyChartWithAnalytics(props: HierarchyChartProps) {
	return (
		<TeamsAppAnalyticsContext data={{ source: 'hierarchyChart' }}>
			<HierarchyChartComponent {...props} />
		</TeamsAppAnalyticsContext>
	);
}

function HierarchyChartComponent({
	data,
	cloudId,
	accountId,
	orgId,
	testId,
	onNodeClick,
	isPreview,
	onViewFullReportingLine,
	requestingUserId,
}: HierarchyChartProps) {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();

	const handleManagerNodeClick = useCallback(
		(clickedAccountId: string) => {
			fireEvent('ui.hierarchyChart.clicked.managerNode', {});
			onNodeClick?.(clickedAccountId);
		},
		[fireEvent, onNodeClick],
	);

	const handleDirectReportNodeClick = useCallback(
		(clickedAccountId: string) => {
			fireEvent('ui.hierarchyChart.clicked.directReportNode', {});
			onNodeClick?.(clickedAccountId);
		},
		[fireEvent, onNodeClick],
	);

	const handlePeerNodeClick = useCallback(
		(clickedAccountId: string) => {
			fireEvent('ui.hierarchyChart.clicked.peerNode', {});
			onNodeClick?.(clickedAccountId);
		},
		[fireEvent, onNodeClick],
	);

	const handleOverflowClick = useCallback(() => {
		fireEvent('ui.hierarchyChart.clicked.overflow', {});
		onViewFullReportingLine?.();
	}, [fireEvent, onViewFullReportingLine]);

	const handleViewFullReportingLineClick = useCallback(() => {
		fireEvent('ui.hierarchyChart.clicked.viewFullReportingLine', {});
		onViewFullReportingLine?.();
	}, [fireEvent, onViewFullReportingLine]);

	const fireViewFullReportingLineEvent = useCallback(() => {
		fireEvent('ui.hierarchyChart.clicked.viewFullReportingLine', {});
	}, [fireEvent]);

	const { managers: managersRaw = [], user, reports = [], peers = [] } = data;
	const managers = [...managersRaw].reverse();
	const hasReports = reports.length > 0;
	const hasPeers = peers.length > 0 && !hasReports;

	const maxManagers = isPreview ? 1 : undefined;
	const maxReports = isPreview ? MAX_REPORT_CARDS : undefined;

	// When isPreview: always show top (CEO) + bottom N managers (skip-level + direct manager).
	// Collapse everything in between with an overflow tile.
	const collapsedManagerLayout =
		maxManagers !== undefined && managers.length > maxManagers + 1
			? {
					top: managers.slice(0, 1),
					hidden: managers.slice(1, managers.length - maxManagers),
					bottom: managers.slice(managers.length - maxManagers),
				}
			: null;
	const hiddenManagerCount = collapsedManagerLayout?.hidden.length ?? 0;

	const { visibleReports, overflow } = useMemo(
		() => partitionReports(reports, maxReports),
		[reports, maxReports],
	);

	// Reserve one slot for the selected user card when capping peers
	const maxPeers = maxReports !== undefined ? maxReports - 1 : undefined;
	const { visibleReports: visiblePeers, overflow: peersOverflow } = useMemo(
		() => partitionReports(peers, maxPeers),
		[peers, maxPeers],
	);

	const fullReportingLineNavigation = useMemo(() => {
		if (!cloudId || !accountId) {
			return undefined;
		}
		return navigateToTeamsApp({
			type: 'USER',
			payload: { userId: accountId },
			cloudId,
		});
	}, [cloudId, accountId]);

	const showFullReportingLineLink = isPreview && !!(accountId && fullReportingLineNavigation?.href);

	return (
		<Stack alignInline="center" testId={testId}>
			{isPreview && onViewFullReportingLine ? (
				<ChartActionsRow>
					<Button
						onClick={handleViewFullReportingLineClick}
						spacing="compact"
						iconBefore={DepartmentIcon}
					>
						{intl.formatMessage(messages.fullReportingLineLink)}
					</Button>
				</ChartActionsRow>
			) : showFullReportingLineLink && fullReportingLineNavigation ? (
				<ChartActionsRow>
					<TeamsLinkButton
						href={fullReportingLineNavigation.href}
						intent="navigation"
						onClick={(event) => {
							fireViewFullReportingLineEvent();
							fullReportingLineNavigation.onNavigate?.(event);
						}}
						spacing="compact"
						iconBefore={DepartmentIcon}
					>
						{intl.formatMessage(messages.fullReportingLineLink)}
					</TeamsLinkButton>
				</ChartActionsRow>
			) : null}

			{collapsedManagerLayout ? (
				<>
					{collapsedManagerLayout.top.map((manager, index) => (
						<React.Fragment key={manager.accountId}>
							<HierarchyUserCard
								user={manager}
								testId={
									index === 0
										? 'hierarchy-manager-card'
										: `hierarchy-manager-card-${manager.accountId}`
								}
								onNodeClick={handleManagerNodeClick}
								cloudId={cloudId}
								orgId={orgId}
								requestingUserId={requestingUserId}
							/>
							<VerticalChartLine />
						</React.Fragment>
					))}
					{hiddenManagerCount > 0 && (
						<>
							<HierarchyReportsOverflowCard
								hiddenReportsPreview={collapsedManagerLayout.hidden}
								hiddenCount={hiddenManagerCount}
								testId="hierarchy-managers-overflow-card"
								onClick={handleOverflowClick}
								centered
							/>
							<VerticalChartLine />
						</>
					)}
					{collapsedManagerLayout.bottom.map((manager) => (
						<React.Fragment key={manager.accountId}>
							<HierarchyUserCard
								user={manager}
								testId={`hierarchy-manager-card-${manager.accountId}`}
								onNodeClick={handleManagerNodeClick}
								cloudId={cloudId}
								orgId={orgId}
								requestingUserId={requestingUserId}
							/>
							<VerticalChartLine />
						</React.Fragment>
					))}
				</>
			) : (
				managers.map((manager, index) => (
					<React.Fragment key={manager.accountId}>
						<HierarchyUserCard
							user={manager}
							testId={
								index === 0
									? 'hierarchy-manager-card'
									: `hierarchy-manager-card-${manager.accountId}`
							}
							onNodeClick={handleManagerNodeClick}
							cloudId={cloudId}
							orgId={orgId}
							requestingUserId={requestingUserId}
						/>
						<VerticalChartLine />
					</React.Fragment>
				))
			)}

			{!hasPeers && (
				<HierarchyUserCard
					user={user}
					selected
					testId="hierarchy-selected-card"
					onNodeClick={onNodeClick}
					cloudId={cloudId}
					orgId={orgId}
					requestingUserId={requestingUserId}
				/>
			)}

			{(hasReports || hasPeers) && (
				<>
					{!hasPeers && <VerticalChartLine />}
					<ReportsList testId={hasPeers ? 'hierarchy-peers-list' : 'hierarchy-reports-list'}>
						{hasReports &&
							visibleReports.map((report) => (
								<HierarchyUserCard
									key={report.accountId}
									user={report}
									testId={`hierarchy-report-card-${report.accountId}`}
									onNodeClick={handleDirectReportNodeClick}
									cloudId={cloudId}
									orgId={orgId}
									requestingUserId={requestingUserId}
								/>
							))}
						{hasPeers && (
							<>
								<HierarchyUserCard
									user={user}
									selected
									testId="hierarchy-selected-card"
									onNodeClick={
										isPreview && onViewFullReportingLine
											? handleViewFullReportingLineClick
											: undefined
									}
									cloudId={cloudId}
									orgId={orgId}
									requestingUserId={requestingUserId}
								/>
								{visiblePeers.map((peer) => (
									<HierarchyUserCard
										key={peer.accountId}
										user={peer}
										testId={`hierarchy-peer-card-${peer.accountId}`}
										onNodeClick={handlePeerNodeClick}
										cloudId={cloudId}
										orgId={orgId}
										requestingUserId={requestingUserId}
									/>
								))}
								{peersOverflow ? (
									<HierarchyReportsOverflowCard
										hiddenReportsPreview={peersOverflow.hiddenPreview}
										hiddenCount={peersOverflow.hiddenCount}
										onClick={handleOverflowClick}
										testId="hierarchy-peers-more-card"
									/>
								) : null}
							</>
						)}
						{overflow ? (
							<HierarchyReportsOverflowCard
								hiddenReportsPreview={overflow.hiddenPreview}
								hiddenCount={overflow.hiddenCount}
								onClick={handleOverflowClick}
								testId="hierarchy-reports-more-card"
							/>
						) : null}
					</ReportsList>
				</>
			)}
		</Stack>
	);
}
